import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useHistory, useLocation } from 'react-router'
import { useAuthContext } from '@/modules/Auth/contexts/AuthContext'
import { useAttendanceContext } from '@/modules/Attendance/contexts/AttendanceContex'
import CustomLayout from '@/components/RebrandedCustomLayout'
import { redirectToCheckout } from '@/modules/Checkout/utils/redirectToCheckout'
import { useFirebaseContext } from '@/modules/Firebase/contexts/FirebaseContext'
import { getTheme, toast, useMediaQuery } from '@conexasaude/conexa-hero'
import { RenderIf } from '@/components/RenderIf'
import screeningService from '../../../../../Screening/screening-services'
import { HeroWrapper } from '@/styles/rebranded/HeroWrapper'
import DataConfirmationCard from '../DataConfirmationCard'
import DataEditidingForm from '../DataEditingForm'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { ConfirmContactWrapperType, UserConfirmDataFormType } from './types'
import { getUserConfirmDataSchema } from './schemas'
import { zodResolver } from '@hookform/resolvers/zod'
import { ISex } from '@/modules/Profile/profile-types'
import { getSexes } from '@/modules/Profile/profile-services'
import { verifyFreeAttendances } from '@/modules/Attendance/services'
import { setCache } from '@/utils/cache'
import { useScreeningData } from '@/modules/Screening/contexts/screeningContext'
import { formatToBRDate } from '@/utils/date'
import { Breadcrumb } from '@/components/RebrandedBreadcrumb'
import {
  getBreadcrumbsFromMedicFlux,
  getBreadcrumbsFromNoPreferenceFlow,
  getBreadcrumbsFromSpecialityFlux,
  getImmediateServiceBreadcrumbWithoutScreening,
} from './utils'

const ConfirmContactWrapper: FunctionComponent<ConfirmContactWrapperType> = ({
  hideBreadcrumb,
  attendanceType,
}) => {
  const history = useHistory()
  const location = useLocation()
  const { user, clinic, updateUser } = useAuthContext()
  const { logAnalyticsEvent } = useFirebaseContext()
  const { setAttendance, attendance } = useAttendanceContext()
  const [isEditing, setIsEditing] = useState(false)
  const [sexOptions, setSexOptions] = useState<ISex[]>([])
  const { screeningData } = useScreeningData()
  const theme = getTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.grid.breakpoint.sm} )`)
  const attendanceTypeText =
    attendanceType === 'immediate' ? 'Imediata' : 'Agendada'

  const [updateLoading, setUpdateLoading] = useState(false)
  const [contactDetails, setContactDetails] = useState<UserConfirmDataFormType>(
    {
      cellphone: user.parsedCellphone || '',
      email: user.email || '',
      cep: user.address.formattedCep || '',
      streetName: user.address.streetName || '',
      number: user.address.number || '',
      complement: user.address.complement || '',
      neighborhood: user.address.neighborhood || '',
      city: user.address.city || '',
      state: user.address.state || '',
      sex: user.sex || '',
      emergencyContactFullName:
        user.contactsEmergency.emergencyContactFullName || '',
      emergencyContactCellphone:
        user.contactsEmergency.emergencyContactCellphone || '',
      relationship: user.contactsEmergency.relationship || '',
      emergencyContactId: user.contactsEmergency.emergencyContactId,
      daysPregnancy: user?.pregnantInformation?.daysPregnancy || '',
      weeksPregnancy: user?.pregnantInformation?.weeksPregnancy || '',
      lastMenstrualPeriodDate:
        user?.pregnantInformation?.lastMenstrualPeriodDate || '',
    },
  )

  const formMethods = useForm<UserConfirmDataFormType>({
    resolver: zodResolver(
      getUserConfirmDataSchema(
        clinic?.flag?.HabilitarObrigatoriedadeEndereco,
        clinic?.flag?.HabilitarObrigatoriedadeContatoEmergencia,
        clinic?.flag?.HabilitarFluxoDadosGestacionais,
        !!user?.pregnantInformation?.lastMenstrualPeriodDate,
      ),
    ),
    mode: 'onChange',
    defaultValues: useMemo(() => {
      return contactDetails
    }, [contactDetails]),
  })

  const getBreadcrumbs = () => {
    if (attendance?.selectedPreferenceType === 'from_without_preference') {
      return getBreadcrumbsFromNoPreferenceFlow(
        attendance.speciality?.id.toLowerCase(),
        isMobile,
        logAnalyticsEvent,
      )
    }

    if (attendance?.selectedPreferenceType === 'from_speciality') {
      return getBreadcrumbsFromSpecialityFlux(
        attendance.speciality?.id.toLowerCase(),
        attendance.medic?.id,
        attendance.selectedSpecialityId,
        isMobile,
        logAnalyticsEvent,
      )
    }

    if (attendance?.selectedPreferenceType === 'from_medic_name') {
      return getBreadcrumbsFromMedicFlux(
        attendance.speciality?.id.toLowerCase(),
        attendance.medic?.id,
        isMobile,
        logAnalyticsEvent,
      )
    }
    if (location?.pathname === '/atendimento-imediato/atualizar-dados') {
      return getImmediateServiceBreadcrumbWithoutScreening(logAnalyticsEvent)
    }

    return []
  }

  const getFormOptionsFromServices = async () => {
    const sexes = await getSexes()
    setSexOptions(sexes)
  }

  useEffect(() => {
    getFormOptionsFromServices()
  }, [])

  useEffect(() => {
    if (!attendance?.speciality) history.push('/')
  }, [history, attendance])

  useEffect(() => {
    formMethods.trigger(['cellphone', 'sex'])
    if (clinic?.flag?.HabilitarObrigatoriedadeEndereco) {
      formMethods.trigger([
        'cep',
        'city',
        'neighborhood',
        'number',
        'streetName',
        'state',
      ])
    }
    if (clinic?.flag?.HabilitarObrigatoriedadeContatoEmergencia) {
      formMethods.trigger([
        'emergencyContactCellphone',
        'emergencyContactFullName',
        'relationship',
      ])
    }
    if (clinic?.flag?.HabilitarFluxoDadosGestacionais) {
      formMethods?.trigger([
        'weeksPregnancy',
        'daysPregnancy',
        'lastMenstrualPeriodDate',
      ])
    }
    if (Object?.values(formMethods?.formState?.errors)?.length) {
      setIsEditing(true)
      toast({
        message: 'Verifique os campos com erro',
        variant: 'error',
        autoCloseDuration: 5000,
        position: 'top-right',
      })
    }
  }, [clinic?.flag, formMethods, formMethods?.formState?.errors])

  const confirmAndAnalyticsEvent = useCallback(async () => {
    setContactDetails({
      cep: contactDetails?.cep,
      streetName: contactDetails.streetName,
      number: contactDetails.number,
      complement: contactDetails.complement,
      neighborhood: contactDetails.neighborhood,
      city: contactDetails.city,
      sex: contactDetails.sex,
      cellphone: contactDetails.cellphone,
      email: contactDetails.email,
      emergencyContactFullName: contactDetails.emergencyContactFullName,
      emergencyContactCellphone: contactDetails.emergencyContactCellphone,
      relationship: contactDetails.relationship,
      state: contactDetails.state,
      emergencyContactId: contactDetails.emergencyContactId,
      daysPregnancy: user?.pregnantInformation?.daysPregnancy,
      weeksPregnancy: user?.pregnantInformation?.weeksPregnancy,
      lastMenstrualPeriodDate: formatToBRDate(
        user?.pregnantInformation?.lastMenstrualPeriodDate,
      ),
    })
    setAttendance(att => {
      att.patientContact = contactDetails
      return att
    })
    // clinica hibrida
    if (attendanceType === 'immediate') {
      const hasFreeAttendances = await verifyFreeAttendances({
        professionalType: screeningData?.professionalType,
        attendanceType: 'IMEDIATO',
        date: attendance?.date,
      })
      setCache('has-free-attendance', hasFreeAttendances)
      const hasCheckoutEnabled = clinic?.flag?.PermiteConsultaParticular
      if (
        hasCheckoutEnabled &&
        hasFreeAttendances === 'limitReached' &&
        !clinic?.flag?.ClinicaUnificadaPV
      ) {
        toast({
          message:
            'Realize o pagamento da consulta. Você atingiu  o limite de atendimentos gratuitos. Finalize esse agendamento, realizando o pagamento.',
          variant: 'warning',
          autoCloseDuration: 5000,
          position: 'top-right',
        })
      }
    }
    redirectToCheckout({
      attendanceType: attendanceType,
      push: history.push,
      hasPayment: clinic?.flag?.PermiteConsultaParticular,
      hasMultipleCards: clinic?.flag?.HabilitarMultiplosCartoesConvenio,
    })
    logAnalyticsEvent(`Pac_${attendanceTypeText}_ConfirmarEContinuar`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendance, contactDetails, setAttendance])

  const editContacts = () => {
    setIsEditing(true)
    logAnalyticsEvent(`Pac_${attendanceTypeText}_EditarDados`)
  }

  const updateUserData = async (
    data: UserConfirmDataFormType,
  ): Promise<void> => {
    setUpdateLoading(true)
    logAnalyticsEvent(`Pac_${attendanceTypeText}_Atualizar`)
    try {
      await screeningService.updateUserDataInformation(
        data,
        user?.contactsEmergency?.emergencyContactId,
      )
      const emergencyContactIdUser =
        await screeningService.getEmergencyContactId()
      updateUser(user => {
        user.cellphone = data.cellphone
        user.sex = data.sex
        user.address.formattedCep = data.cep
        user.address.streetName = data.streetName
        user.address.number = data.number
        user.address.complement = data.complement
        user.address.neighborhood = data.neighborhood
        user.address.city = data.city
        user.address.state = data.state
        user.contactsEmergency.emergencyContactFullName =
          data.emergencyContactFullName
        user.contactsEmergency.emergencyContactCellphone =
          data.emergencyContactCellphone
        user.contactsEmergency.relationship = data.relationship
        user.contactsEmergency.emergencyContactId =
          emergencyContactIdUser || null
        user.pregnantInformation.daysPregnancy = data?.daysPregnancy
        user.pregnantInformation.weeksPregnancy = data?.weeksPregnancy
        user.pregnantInformation.lastMenstrualPeriodDate = formatToBRDate(
          data?.lastMenstrualPeriodDate,
        )

        return user
      })
      toast({
        message: 'Alterações salvas com sucesso!',
        variant: 'success',
        autoCloseDuration: 5000,
        position: 'top-right',
      })
      setIsEditing(false)
    } catch (err) {
      const errorMessage =
        err?.response?.data?.object?.msg ||
        err?.response?.data?.msg ||
        err?.message

      toast({
        message: errorMessage,
        variant: 'error',
        autoCloseDuration: 5000,
        position: 'top-right',
      })
    } finally {
      setUpdateLoading(false)
    }
  }

  const onSubmit: SubmitHandler<UserConfirmDataFormType> = data => {
    try {
      if (isEditing) {
        updateUserData(data)
      } else {
        confirmAndAnalyticsEvent()
      }
    } catch (error) {
      console.log(error)
    }
  }

  return (
    <HeroWrapper>
      <FormProvider {...formMethods}>
        <form>
          <CustomLayout
            hasPrimaryAction
            primaryActionLabel={
              isEditing ? 'Atualizar' : 'Confirmar e continuar'
            }
            primaryAction={formMethods.handleSubmit(onSubmit)}
            primaryActionProps={{
              disabled: isEditing && updateLoading,
            }}
            hasLayoutMenu
            layoutMenu={
              !hideBreadcrumb ? <Breadcrumb items={getBreadcrumbs()} /> : null
            }
          >
            <RenderIf condition={!isEditing}>
              <DataConfirmationCard
                handleClick={editContacts}
                sexes={sexOptions}
              />
            </RenderIf>
            <RenderIf condition={isEditing}>
              <DataEditidingForm
                sexes={sexOptions}
                hidePregnancyForm={
                  !clinic?.flag?.HabilitarFluxoDadosGestacionais
                }
              />
            </RenderIf>
          </CustomLayout>
        </form>
      </FormProvider>
    </HeroWrapper>
  )
}

export default ConfirmContactWrapper
